import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, describeCommercialError, describeError, newIdempotencyKey } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { Badge } from '@/components/ui';
import { LimitOverrideCheckbox } from '@/components/LimitOverrideCheckbox';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { selectCurrentRole, useAuthStore, useHasPlatformPermission } from '@/stores/auth';

type Extension = {
  id: string;
  previousExpiresAt: string | null;
  newExpiresAt: string;
  reactivatedFromExpired: boolean;
  reason: string;
  isCancelled: boolean;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
};

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '—';
}

export function TrialExtendPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  // Estender e cancelar extensão exigem a mesma permissão na API; o histórico
  // é de todo operador (change platform-backoffice-hardening, C1).
  const canExtend = useHasPlatformPermission('platform.trial.extend');
  // Só o Admin passa da regra de uma extensão vigente, com a exceção marcada (C2); a mensagem do
  // 422 oferece esse caminho a ele, como a caixa "acima do teto" que só ele vê.
  const isAdmin = useAuthStore(selectCurrentRole) === 'Admin';
  const [extendUntil, setExtendUntil] = useState('');
  const [reason, setReason] = useState('');
  const [limitOverride, setLimitOverride] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idempotency = useIdempotencyKey();
  const [history, setHistory] = useState<Extension[]>([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}/trial/extensions`, {
        params: { page: 1, pageSize: 50 },
      });
      setHistory(data.items ?? []);
    } catch {
      // The history is context, not the point of the screen — a failure here
      // must not block the operator from extending the trial.
    }
  }, [tenantId]);

  /*
   * Falso positivo da regra, nao divida: todo `setState` deste callback roda
   * DEPOIS do `await`, que e o que a propria documentacao aponta como valido.
   * O bug esta registrado em facebook/react#34905, ainda aberto.
   *
   * Revisar quando o plugin subir de versao: se a supressao virar "unused
   * eslint-disable", e sinal de que corrigiram e a linha pode sair.
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- ver nota acima
    void loadHistory();
  }, [loadHistory]);

  async function cancelExtension(id: string) {
    const why = window.prompt('Motivo do cancelamento (obrigatório):');
    if (!why || !why.trim()) return;
    setError(null);
    try {
      await api.post(
        `/platform/tenants/${tenantId}/trial/extensions/${id}/cancel`,
        { reason: why.trim() },
        { headers: { 'Idempotency-Key': newIdempotencyKey() } },
      );
      await loadHistory();
    } catch (err) {
      setError(describeError(err));
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const url = `/platform/tenants/${tenantId}/trial/extend`;
      const body = {
        extendUntil: new Date(extendUntil).toISOString(),
        reason: reason.trim(),
        // Só a exceção do Admin leva a marca; sem ela, o campo nem vai.
        ...(limitOverride ? { limitOverride: true } : {}),
      };
      // A mesma chave na repetição do mesmo pedido (ver useIdempotencyKey).
      await api.post(url, body, { headers: { 'Idempotency-Key': idempotency.keyFor(url, body) } });
      idempotency.settle();
      setSuccess(true);
      setLimitOverride(false);
      await loadHistory();
    } catch (err) {
      idempotency.settle(err);
      setError(describeCommercialError(err, 'days', { canOverride: isAdmin }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page" style={{ maxWidth: 640 }}>
      <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        {canExtend ? 'Estender trial' : 'Extensões de trial'}
      </h2>

      {!canExtend ? (
        <p className="muted" style={{ margin: 0 }}>
          Seu perfil consulta o histórico, mas não estende nem cancela extensões de trial.
        </p>
      ) : success ? (
        <div className="card">
          <div className="info-banner" style={{ background: '#DCFCE7', color: '#15803D', borderColor: '#BBF7D0' }}>
            Trial estendido com sucesso.
          </div>
          <button className="btn" onClick={() => navigate(`/tenants/${tenantId}`)}>Voltar ao tenant</button>
        </div>
      ) : (
        <form className="card" onSubmit={onSubmit}>
          {error && <div className="error-banner">{error}</div>}

          <div className="field">
            <label htmlFor="extendUntil">Nova data de expiração</label>
            <input
              id="extendUntil"
              type="datetime-local"
              value={extendUntil}
              onChange={(e) => setExtendUntil(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="reason">Motivo (obrigatório)</label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              minLength={5}
            />
          </div>

          <LimitOverrideCheckbox checked={limitOverride} onChange={setLimitOverride} />

          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? 'Estendendo…' : 'Estender trial'}
          </button>
        </form>
      )}

      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Extensoes anteriores</h3>
        {history.length === 0 ? (
          <p>Nenhuma extensao registrada.</p>
        ) : (
          <div className="table-scroll">
            <CardTable className="table">
              <THead>
                <Tr>
                  <Th>Status</Th>
                  <Th>De - para</Th>
                  <Th>Motivo</Th>
                  <Th>Cancelamento</Th>
                  <Th />
                </Tr>
              </THead>
              <TBody>
                {history.map((h) => (
                  <Tr key={h.id}>
                    <Td heading>
                      {h.isCancelled
                        ? <Badge tone="error">Cancelada</Badge>
                        : <Badge tone="success">Vigente</Badge>}
                      {h.reactivatedFromExpired && !h.isCancelled && (
                        <> <Badge tone="info">Reativou</Badge></>
                      )}
                    </Td>
                    <Td label="De - para">{formatDate(h.previousExpiresAt)} - {formatDate(h.newExpiresAt)}</Td>
                    <Td label="Motivo">{h.reason}</Td>
                    <Td label="Cancelamento">{h.cancellationReason ?? '-'}</Td>
                    <Td action>
                      {canExtend && !h.isCancelled && (
                        <button className="btn btn-secondary btn-sm" onClick={() => void cancelExtension(h.id)}>
                          Cancelar
                        </button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </CardTable>
          </div>
        )}
      </div>
    </div>
  );
}
